import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { 
  Message, 
  ModelName, 
  UseChatOptions, 
  UseChatReturn,
  FileAttachment
} from '../types';
import { ChatApiService } from '../api/chat';
import { DEFAULT_MODEL } from '../utils/constants';

// Create API client instance
const chatApiService = new ChatApiService();

export const useChat = (options: UseChatOptions = {}): UseChatReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedModel = DEFAULT_MODEL as ModelName;
  
  // Генерируем новый thread_id при первом запуске или загружаем из localStorage
  const [threadId] = useState(() => {
    const savedThreadId = localStorage.getItem('chat_thread_id');
    // Если есть сохраненный thread_id, используем его, иначе генерируем новый
    return savedThreadId || uuidv4();
  });
  
  const historyLoadedRef = useRef(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const sendMessage = useCallback(async (content: string, attachments?: FileAttachment[]) => {
    if (!content.trim() || isLoading) return;

    const userMessage: Message = {
      id: uuidv4(),
      type: 'human',
      content,
      timestamp: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      attachments,
    };

    setMessages(prev => {
      return [...prev, userMessage];
    });
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      const isFirstMessage = messages.length === 0;
      
      const result = await chatApiService.sendMessage(
        content,
        selectedModel,
        threadId,
        attachments,
        undefined,
        isFirstMessage
      );

      const aiMessage: Message = {
        id: uuidv4(),
        type: 'ai',
        content: result.response,
        timestamp: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
        attachments: result.attachments?.map(att => ({
          ...att,
          id: String(att.id),
          uploadedAt: att.uploadedAt || new Date(),
        })),
      };

      setMessages(prev => {
        const newMessages = [...prev, aiMessage];
        
        // Сохраняем thread_id после успешной отправки первого сообщения
        // Проверяем, что это первая пара сообщений (пользователь + AI)
        if (prev.length === 1) {
          localStorage.setItem('chat_thread_id', threadId);
        }
        
        return newMessages;
      });
      
      if (options.onMessage) {
        options.onMessage(aiMessage);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      
      if (options.onError) {
        options.onError(err instanceof Error ? err : new Error(errorMessage));
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, selectedModel, threadId, options, messages.length]);


  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    // Очищаем сохраненный thread_id при очистке сообщений
    localStorage.removeItem('chat_thread_id');
    // Сбрасываем флаг загрузки истории
    historyLoadedRef.current = false;
  }, []);

  // Загружаем историю чата при инициализации
  useEffect(() => {
    const savedThreadId = localStorage.getItem('chat_thread_id');
    
    if (savedThreadId && !historyLoadedRef.current) {
      // Показываем лоадер только если есть сохраненный thread_id
      setIsLoadingHistory(true);
      historyLoadedRef.current = true;
      
      chatApiService.getChatHistory(savedThreadId)
        .then(history => {
          if (history && history.length > 0) {
            setMessages(history);
          }
          setIsLoadingHistory(false);
        })
        .catch((error) => {
          console.error('Error loading history:', error);
          historyLoadedRef.current = false; // Сбрасываем флаг при ошибке
          setIsLoadingHistory(false);
        });
    }
    // Если нет сохраненного thread_id, лоадер не показываем вообще
  }, []); // Убираем зависимость от messages.length

  return {
    messages,
    inputValue,
    setInputValue,
    isLoading,
    isLoadingHistory,
    sendMessage,
    selectedModel,
    threadId,
    error,
    clearError,
    clearMessages,
  };
};